import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { bugsnagClient } from "../bugsnag";
import { BACKEND_URL } from "../config/config";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import { useExcelExport } from "../hooks/useExcelExport";
import { useProgressData } from "../hooks/useProgressData";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/api";

const PlanManagement = ({
  sectionData,
  projectId,
  canManage,
  onSectionUpdate,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { downloadExcel } = useExcelExport({
    planName: sectionData.name,
    projectId: projectId,
    sectionId: sectionData._id,
    planPreviewUrl: sectionData.plan?.previewUrl,
  });

  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [selectedDwgFile, setSelectedDwgFile] = useState(null);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [selectedPreviewFile, setSelectedPreviewFile] = useState(null);
  const [isUploadingPreview, setIsUploadingPreview] = useState(false);
  const [chessboardJson, setChessboardJson] = useState("");
  const [isUpdatingChessboard, setIsUpdatingChessboard] = useState(false);
  const [showChessboardEditor, setShowChessboardEditor] = useState(false);

  // Get progress data for analytics overlay
  const progressData = useProgressData(projectId, sectionData._id);

  const handlePdfSelection = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSelectedPdfFile(null);
      return;
    }

    // Validate file type
    const fileExtension = file.name.toLowerCase().slice(-4);
    if (!(fileExtension === ".pdf" || file.type === "application/pdf")) {
      setError("Please select a valid PDF file");
      setSelectedPdfFile(null);
      event.target.value = "";
      return;
    }

    // Validate file size
    if (file.size > 50 * 1024 * 1024) {
      setError(`PDF file ${file.name} is too large. Maximum size is 50MB.`);
      setSelectedPdfFile(null);
      event.target.value = "";
      return;
    }

    // Check if section already has plans
    const hasExistingPlans = sectionData.plan && sectionData.plan.planId;

    if (hasExistingPlans) {
      setError("Section already has plans. Remove existing plans first.");
      setSelectedPdfFile(null);
      event.target.value = "";
      return;
    }

    setSelectedPdfFile(file);
    setError("");
  };

  const handleDwgSelection = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSelectedDwgFile(null);
      return;
    }

    // Validate file type
    const fileExtension = file.name.toLowerCase().slice(-4);
    if (
      !(
        fileExtension === ".dwg" ||
        file.type.includes("dwg") ||
        file.type.includes("autocad")
      )
    ) {
      setError("Please select a valid DWG file");
      setSelectedDwgFile(null);
      event.target.value = "";
      return;
    }

    // Validate file size
    if (file.size > 50 * 1024 * 1024) {
      setError(`DWG file ${file.name} is too large. Maximum size is 50MB.`);
      setSelectedDwgFile(null);
      event.target.value = "";
      return;
    }

    // Check if section already has plans
    const hasExistingPlans = sectionData.plan && sectionData.plan.planId;

    if (hasExistingPlans) {
      setError("Section already has plans. Remove existing plans first.");
      setSelectedDwgFile(null);
      event.target.value = "";
      return;
    }

    setSelectedDwgFile(file);
    setError("");
  };

  const handleUpload = async () => {
    if (!selectedPdfFile || !selectedDwgFile) {
      setError("Please select both PDF and DWG files before uploading");
      return;
    }

    try {
      setIsUploading(true);
      setError("");

      const formData = new FormData();
      // Add files as separate fields
      if (selectedPdfFile) {
        formData.append("pdf", selectedPdfFile);
      }
      if (selectedDwgFile) {
        formData.append("dwg", selectedDwgFile);
      }

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const data = await api.uploadSectionPlan(
        projectId,
        sectionData._id,
        formData,
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      setUploadProgress(100);

      trackEvent(ANALYTICS_EVENTS.PLAN_UPLOADED, {
        projectId,
        sectionId: sectionData._id,
      });

      // Update section data with new plans
      onSectionUpdate(data.section);

      // Clear selected files and file inputs
      setSelectedPdfFile(null);
      setSelectedDwgFile(null);
      const pdfInput = document.getElementById("pdf-upload");
      const dwgInput = document.getElementById("dwg-upload");
      if (pdfInput) pdfInput.value = "";
      if (dwgInput) dwgInput.value = "";

      // Reset upload progress after a delay
      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);
    } catch (error) {
      // Upload error occurred
      setError(error.message || "Upload failed");
      setUploadProgress(0);
      bugsnagClient.notify(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePlan = async () => {
    if (
      !window.confirm(
        "Are you sure you want to remove this plan? This will delete both PDF and DWG files. This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/projects/${projectId}/sections/${sectionData._id}/plan`,
        {
          method: "DELETE",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remove plan");
      }

      const data = await response.json();
      data.section._id = data.section.id.toString();
      trackEvent(ANALYTICS_EVENTS.PLAN_REMOVED, {
        projectId,
        sectionId: sectionData._id,
      });
      onSectionUpdate(data.section);
    } catch (error) {
      // Remove plan error occurred
      setError(error.message || "Failed to remove plan");
      bugsnagClient.notify(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadOutputResult = async (printVersion = false) => {
    try {
      setLoading(true);

      // Fetch data on-demand for export
      const [objectsResponse, documentationsResponse] = await Promise.all([
        api.fetchDetectedObjectsBySection(projectId, sectionData._id),
        api.getDocumentationBySection(sectionData._id, { includeFrames: true }),
      ]);

      const detectedObjects = objectsResponse.data;
      const sectionDocumentations = documentationsResponse.documentation;

      const documentations = {};
      sectionDocumentations.forEach((documentation) => {
        documentations[documentation._id] = documentation.frames.map(
          (frame) => ({
            frameName: frame.frameName,
            objects: frame.objects.map((object) => ({
              id: object.detectedObjectId._id,
              bndbox: object.bndbox,
            })),
          }),
        );
      });
      trackEvent(ANALYTICS_EVENTS.EXCEL_EXPORTED, {
        projectId,
        sectionId: sectionData._id,
        printVersion,
      });
      await downloadExcel(detectedObjects, printVersion, documentations);
    } catch (error) {
      console.log("error", error);
      toast.error(error.message);
      bugsnagClient.notify(error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Map frontend status to backend status (for API calls)
  const frontendToBackendStatus = (frontendStatus) => {
    const statusMap = {
      processed: "done",
      processing: "in_process",
      failed: "error",
      created: "created",
      in_validation: "in_validation",
    };
    return statusMap[frontendStatus] || frontendStatus;
  };

  // Map backend status to frontend display
  const getStatusDisplay = (status) => {
    const statusMap = {
      done: "Processed",
      processed: "Processed",
      in_process: "Processing",
      processing: "Processing",
      in_validation: "In Validation",
      created: "Created",
      error: "Failed",
      failed: "Failed",
    };
    return statusMap[status] || status;
  };

  // Get status color
  const getStatusColor = (status) => {
    if (status === "done" || status === "processed") {
      return "bg-green-100 text-green-800";
    } else if (
      status === "in_process" ||
      status === "processing" ||
      status === "in_validation"
    ) {
      return "bg-yellow-100 text-yellow-800";
    } else {
      return "bg-red-100 text-red-800";
    }
  };

  // Get current backend status (convert frontend status back to backend)
  const getCurrentBackendStatus = () => {
    return frontendToBackendStatus(sectionData.plan.status);
  };

  const handleStatusChange = async (newBackendStatus) => {
    if (!sectionData.plan?.id && !sectionData.plan?.planId) {
      toast.error("Plan ID not found");
      return;
    }

    const floorPlanId = sectionData.plan.id || sectionData.plan.planId;

    try {
      setLoading(true);
      await api.updateFloorPlanStatus(floorPlanId, newBackendStatus);

      // The response should have the updated plan, but we need to refresh
      // to get the adapter-converted status. For now, update with backend status
      // and let the adapter handle conversion on next fetch
      const updatedPlan = {
        ...sectionData.plan,
        status: newBackendStatus, // Store backend status temporarily
      };
      const updatedSection = {
        ...sectionData,
        plan: updatedPlan,
      };
      onSectionUpdate(updatedSection);

      toast.success("Plan status updated successfully");
    } catch (error) {
      console.error("Error updating plan status:", error);
      toast.error(error.message || "Failed to update plan status");
      bugsnagClient.notify(error);
    } finally {
      setLoading(false);
    }
  };

  const isGlobalAdmin = user?.isGlobalAdmin;

  const handlePreviewSelection = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSelectedPreviewFile(null);
      return;
    }

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    const fileExtension = file.name.toLowerCase().slice(-5);
    const isValidImage =
      allowedTypes.includes(file.type) ||
      [".png", ".jpg", ".jpeg", ".webp"].some((ext) =>
        fileExtension.endsWith(ext),
      );

    if (!isValidImage) {
      setError("Please select a valid image file (PNG, JPG, JPEG, or WEBP)");
      setSelectedPreviewFile(null);
      event.target.value = "";
      return;
    }

    setSelectedPreviewFile(file);
    setError("");
  };

  const handlePreviewUpload = async () => {
    if (!selectedPreviewFile) {
      setError("Please select an image file");
      return;
    }

    if (!sectionData.plan?.id && !sectionData.plan?.planId) {
      toast.error("Plan ID not found");
      return;
    }

    const floorPlanId = sectionData.plan.id || sectionData.plan.planId;

    try {
      setIsUploadingPreview(true);
      setError("");

      const response = await api.updateFloorPlanPreview(
        floorPlanId,
        selectedPreviewFile,
      );

      // Update local state with new preview URL
      const updatedPlan = {
        ...sectionData.plan,
        previewUrl: response.floorPlan.previewUrl,
        hasPreview: !!response.floorPlan.previewUrl,
      };
      const updatedSection = {
        ...sectionData,
        plan: updatedPlan,
      };
      onSectionUpdate(updatedSection);

      setSelectedPreviewFile(null);
      const previewInput = document.getElementById("preview-upload");
      if (previewInput) previewInput.value = "";

      toast.success("Preview image updated successfully");
    } catch (error) {
      console.error("Error updating preview:", error);
      setError(error.message || "Failed to update preview image");
      toast.error(error.message || "Failed to update preview image");
      bugsnagClient.notify(error);
    } finally {
      setIsUploadingPreview(false);
    }
  };

  const handleChessboardUpdate = async () => {
    if (!sectionData.plan?.id && !sectionData.plan?.planId) {
      toast.error("Plan ID not found");
      return;
    }

    let parsedChessboard;
    try {
      parsedChessboard = JSON.parse(chessboardJson);
    } catch (parseError) {
      setError("Invalid JSON format. Please check your input.");
      toast.error("Invalid JSON format");
      return;
    }

    // Validate chessboard structure
    if (
      !parsedChessboard ||
      typeof parsedChessboard !== "object" ||
      !parsedChessboard.x ||
      !parsedChessboard.y ||
      typeof parsedChessboard.x !== "object" ||
      typeof parsedChessboard.y !== "object"
    ) {
      setError("Invalid chessboard format. Expected { x: {...}, y: {...} }");
      toast.error("Invalid chessboard format");
      return;
    }

    const floorPlanId = sectionData.plan.id || sectionData.plan.planId;

    try {
      setIsUpdatingChessboard(true);
      setError("");

      const response = await api.updateFloorPlanChessboard(
        floorPlanId,
        parsedChessboard,
      );

      // Update local state
      const updatedPlan = {
        ...sectionData.plan,
        chessboard: response.floorPlan.chessboard,
      };
      const updatedSection = {
        ...sectionData,
        plan: updatedPlan,
      };
      onSectionUpdate(updatedSection);

      setShowChessboardEditor(false);
      setChessboardJson("");
      toast.success("Chessboard updated successfully");
    } catch (error) {
      console.error("Error updating chessboard:", error);
      setError(error.message || "Failed to update chessboard");
      toast.error(error.message || "Failed to update chessboard");
      bugsnagClient.notify(error);
    } finally {
      setIsUpdatingChessboard(false);
    }
  };

  // Initialize chessboard JSON when editor opens
  const handleOpenChessboardEditor = () => {
    const currentChessboard = sectionData.plan?.chessboard;
    if (currentChessboard) {
      setChessboardJson(JSON.stringify(currentChessboard, null, 2));
    } else {
      setChessboardJson(
        JSON.stringify(
          {
            x: {},
            y: {},
          },
          null,
          2,
        ),
      );
    }
    setShowChessboardEditor(true);
    setError("");
  };

  return (
    <>
      <div className="space-y-6">
        {sectionData.plan && sectionData.plan.planId ? (
          <div className="space-y-4">
            {/* <h3 className="text-lg font-medium text-gray-900">
              {t("section_detail.current_plan")}
            </h3> */}

            {/* Two Column Layout: Plan Preview on Left, Actions on Right */}
            <div className="bg-white border rounded-lg overflow-hidden">
              {/* Plan Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {t("section_detail.current_plan")}
                  </h4>
                  <p className="text-sm text-gray-500">
                    Uploaded on{" "}
                    {new Date(sectionData.plan.uploadDate).toLocaleDateString(
                      undefined,
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )}
                  </p>
                </div>
                {isGlobalAdmin ? (
                  <div className="flex items-center space-x-2">
                    <select
                      value={getCurrentBackendStatus()}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={loading}
                      className={`px-3 py-1 rounded-full text-sm font-medium border-0 focus:ring-2 focus:ring-blue-500 ${getStatusColor(
                        getCurrentBackendStatus(),
                      )} cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <option value="created">Created</option>
                      <option value="in_process">Processing</option>
                      <option value="in_validation">In Validation</option>
                      <option value="done">Done</option>
                      <option value="error">Error</option>
                    </select>
                  </div>
                ) : (
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                      sectionData.plan.status,
                    )}`}
                  >
                    {getStatusDisplay(sectionData.plan.status)}
                  </span>
                )}
              </div>

              {/* Main Content: Image Left, Buttons Right */}
              <div className="flex flex-col lg:flex-row">
                {/* Left Side - Plan Preview */}
                <div className="lg:w-1/2 bg-gray-50 p-8 flex flex-col items-center justify-center border-r space-y-4">
                  {sectionData.plan.hasPreview &&
                  sectionData.plan.previewUrl ? (
                    <div className="relative group w-full">
                      <img
                        src={sectionData.plan.previewUrl}
                        crossOrigin="anonymous"
                        alt={`Preview of ${sectionData.plan.fileName}`}
                        className="w-full h-auto object-contain rounded-lg shadow-lg border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition-all"
                        onClick={() => {
                          if (
                            canManage &&
                            sectionData.plan.status === "processed"
                          ) {
                            navigate(
                              `/project/${projectId}/sections/${sectionData._id}/progress-report`,
                            );
                          }
                        }}
                      />

                      {canManage && sectionData.plan.status === "processed" && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-200 flex items-center justify-center pointer-events-none">
                          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center space-x-2">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                              />
                            </svg>
                            <span className="font-medium">
                              {t("section_detail.open_progress_report")}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-32 h-32 mx-auto bg-gray-200 rounded-lg flex items-center justify-center text-6xl mb-4">
                        📄
                      </div>
                      <p className="text-gray-500">No preview available</p>
                    </div>
                  )}

                  {/* Global Admin Preview Upload */}
                  {isGlobalAdmin && (
                    <div className="w-full border-t pt-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Update Preview Image
                        </label>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handlePreviewSelection}
                          disabled={isUploadingPreview}
                          className="hidden"
                          id="preview-upload"
                        />
                        {!selectedPreviewFile ? (
                          <label
                            htmlFor="preview-upload"
                            className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${
                              isUploadingPreview
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            Choose Image
                          </label>
                        ) : (
                          <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                                  🖼️
                                </div>
                                <div className="flex-1 text-left">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {selectedPreviewFile.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(selectedPreviewFile.size)}
                                  </p>
                                </div>
                                <button
                                  onClick={() => {
                                    setSelectedPreviewFile(null);
                                    document.getElementById(
                                      "preview-upload",
                                    ).value = "";
                                  }}
                                  disabled={isUploadingPreview}
                                  className="text-gray-400 hover:text-blue-500"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={handlePreviewUpload}
                              disabled={isUploadingPreview}
                              className={`w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                isUploadingPreview
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-blue-600 hover:bg-blue-700"
                              }`}
                            >
                              {isUploadingPreview
                                ? "Uploading..."
                                : "Upload Preview"}
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-gray-500">
                          PNG, JPG, JPEG, WEBP
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Global Admin Chessboard Update */}
                  {isGlobalAdmin && (
                    <div className="w-full border-t pt-4 mt-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Update Chessboard
                        </label>
                        {!showChessboardEditor ? (
                          <button
                            onClick={handleOpenChessboardEditor}
                            disabled={isUpdatingChessboard}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                            Edit Chessboard
                          </button>
                        ) : (
                          <div className="space-y-2">
                            <textarea
                              value={chessboardJson}
                              onChange={(e) =>
                                setChessboardJson(e.target.value)
                              }
                              disabled={isUpdatingChessboard}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono text-gray-900 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                              rows={12}
                              placeholder='{\n  "x": { "F": 460, "G": 884 },\n  "y": { "31": 5151, "32": 4797 }\n}'
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={handleChessboardUpdate}
                                disabled={isUpdatingChessboard}
                                className={`flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                  isUpdatingChessboard
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                                }`}
                              >
                                {isUpdatingChessboard
                                  ? "Updating..."
                                  : "Update Chessboard"}
                              </button>
                              <button
                                onClick={() => {
                                  setShowChessboardEditor(false);
                                  setChessboardJson("");
                                  setError("");
                                }}
                                disabled={isUpdatingChessboard}
                                className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Side - Action Buttons and Progress Summary */}
                <div className="lg:w-1/2 p-8 flex flex-col justify-between space-y-6">
                  {/* Progress Summary Section */}
                  {!progressData.isLoading && progressData.totals.total > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {t("section_detail.progress_summary")}
                          </h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t("section_detail.overall_completion_status")}
                          </p>
                        </div>
                        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-600">
                          <span className="text-white text-base font-semibold">
                            {progressData.overallPct}%
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {t("section_detail.installed")}:
                          </span>
                          <span className="text-xl font-bold text-gray-900">
                            {progressData.totals.installed}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {t("section_detail.remaining")}:
                          </span>
                          <span className="text-xl font-bold text-teal-700">
                            {progressData.remaining}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {t("section_detail.deviation")}:
                          </span>
                          <span className="text-xl font-bold text-rose-700">
                            {progressData.totals.real_vs_plan_dev}
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-600 transition-all duration-500"
                            style={{
                              width: `${progressData.overallPct}%`,
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Open Progress Report Button */}
                      <button
                        onClick={() =>
                          navigate(
                            `/project/${projectId}/sections/${sectionData._id}/progress-report`,
                          )
                        }
                        disabled={
                          sectionData.plan.status !== "processed" || loading
                        }
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        <span>{t("section_detail.open_progress_report")}</span>
                      </button>
                    </div>
                  )}

                  {/* Action Buttons - Stacked Vertically */}
                  {canManage && (
                    <div className="flex flex-col space-y-3">
                      {/* <button
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-600 bg-white hover:bg-orange-50 disabled:opacity-50"
                        onClick={() =>
                          navigate(
                            `/project/${projectId}/sections/${sectionData._id}/dashboard`
                          )
                        }
                        disabled={
                          sectionData.plan.status !== "processed" || loading
                        }
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 19h16M4 19V5"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 15l4-4 3 3 5-7"
                          />
                        </svg>
                        {t("section_detail.dashboard_view")}
                      </button> */}

                      <div className="relative">
                        <button
                          onClick={() =>
                            setExportDropdownOpen(!exportDropdownOpen)
                          }
                          className="w-full inline-flex items-center justify-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                          disabled={
                            loading || sectionData.plan.status !== "processed"
                          }
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          {t("export.download_report")}
                          <svg
                            className="w-4 h-4 ml-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>

                        {exportDropdownOpen && (
                          <div className="absolute left-0 bottom-full mb-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handleDownloadOutputResult(false);
                                  setExportDropdownOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                              >
                                <svg
                                  className="w-4 h-4 mr-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                <div className="flex-1">
                                  <div>{t("export.digital_version")}</div>
                                  <div className="text-xs text-gray-500">
                                    {t("export.with_links")}
                                  </div>
                                </div>
                              </button>
                              <button
                                onClick={() => {
                                  handleDownloadOutputResult(true);
                                  setExportDropdownOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                              >
                                <svg
                                  className="w-4 h-4 mr-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H9.5a2 2 0 01-2-2V5a2 2 0 012-2H14a2 2 0 012 2v2"
                                  />
                                </svg>
                                <div className="flex-1">
                                  <div>{t("export.print_version")}</div>
                                  <div className="text-xs text-gray-500">
                                    {t("export.with_images")}
                                  </div>
                                </div>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={handleRemovePlan}
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Remove Plan
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Show upload interface */
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-gray-900">
              {t("section_detail.upload_plan_title")}
            </h3>
            <p className="text-sm text-gray-600">
              Upload both PDF and DWG files for a complete plan. Both file
              formats are required.
            </p>

            {canManage ? (
              <div className="space-y-6">
                <h4 className="text-lg font-medium text-gray-900 text-center">
                  Upload Complete Plan (PDF + DWG)
                </h4>

                {/* Two File Upload Areas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* PDF Upload Area */}
                  <div className="border-2 border-dashed border-red-300 rounded-lg p-6 text-center hover:border-red-400 transition-colors bg-red-50">
                    <div className="text-4xl mb-3">📄</div>
                    <h5 className="text-lg font-medium text-gray-900 mb-2">
                      PDF File
                    </h5>
                    <p className="text-sm text-gray-600 mb-4">
                      Readable format with preview
                    </p>

                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfSelection}
                      disabled={isUploading}
                      className="hidden"
                      id="pdf-upload"
                    />

                    {!selectedPdfFile ? (
                      <label
                        htmlFor="pdf-upload"
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                          isUploading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-red-600 hover:bg-red-700 cursor-pointer"
                        }`}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        Choose PDF File
                      </label>
                    ) : (
                      <div className="bg-white rounded-lg p-3 border border-red-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-red-600">
                            📄
                          </div>
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {selectedPdfFile.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(selectedPdfFile.size)}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedPdfFile(null);
                              document.getElementById("pdf-upload").value = "";
                            }}
                            disabled={isUploading}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">Max 50MB</p>
                  </div>

                  {/* DWG Upload Area */}
                  <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors bg-blue-50">
                    <div className="text-4xl mb-3">📐</div>
                    <h5 className="text-lg font-medium text-gray-900 mb-2">
                      DWG File
                    </h5>
                    <p className="text-sm text-gray-600 mb-4">
                      CAD format for technical use
                    </p>

                    <input
                      type="file"
                      accept=".dwg"
                      onChange={handleDwgSelection}
                      disabled={isUploading}
                      className="hidden"
                      id="dwg-upload"
                    />

                    {!selectedDwgFile ? (
                      <label
                        htmlFor="dwg-upload"
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                          isUploading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 cursor-pointer"
                        }`}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        Choose DWG File
                      </label>
                    ) : (
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                            📐
                          </div>
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {selectedDwgFile.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(selectedDwgFile.size)}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedDwgFile(null);
                              document.getElementById("dwg-upload").value = "";
                            }}
                            disabled={isUploading}
                            className="text-gray-400 hover:text-blue-500"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">Max 50MB</p>
                  </div>
                </div>

                {/* Upload Button */}
                <div className="text-center">
                  <button
                    onClick={handleUpload}
                    disabled={
                      isUploading || !selectedPdfFile || !selectedDwgFile
                    }
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                      isUploading || !selectedPdfFile || !selectedDwgFile
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    {isUploading ? "Uploading..." : "Upload Complete Plan"}
                  </button>

                  <button
                    onClick={() => {
                      setSelectedPdfFile(null);
                      setSelectedDwgFile(null);
                      setError("");
                      const pdfInput = document.getElementById("pdf-upload");
                      const dwgInput = document.getElementById("dwg-upload");
                      if (pdfInput) pdfInput.value = "";
                      if (dwgInput) dwgInput.value = "";
                    }}
                    disabled={isUploading}
                    className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Clear All
                  </button>
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {t("section_detail.uploading")}
                      </span>
                      <span className="text-gray-600">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t("section_detail.upload_restricted_title")}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t("section_detail.upload_restricted_desc")}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PlanManagement;
